import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from '../contexts/TranslationContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { API_BASE } from '../lib/apiBase';
import { 
    Inbox,
    FileText,
    Link2,
    RefreshCw,
    ExternalLink,
    Search,
    Filter,
    CheckCircle,
    AlertTriangle,
    Clock,
    XCircle,
    Eye,
    Download,
    Trash2,
    ArrowLeft
} from 'lucide-react';

const STATUS_CONFIG = {
    linked: { label: 'Linked', icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    needs_review: { label: 'Needs Review', icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50' },
    pending: { label: 'Pending', icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50' },
    failed: { label: 'Failed', icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
};

const DOCUMENT_TYPE_LABELS = {
    ncb_clearance: 'NCB Clearance',
    invoice: 'Invoice',
    delivery_note: 'Delivery Note',
    production_confirmation: 'Production Confirmation',
    artwork_approval: 'Artwork Approval',
    shipping_notice: 'Shipping Notice',
    other: 'Other',
};

// Status badge component - defined outside the main component
const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = config.icon;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.color}`}>
            <Icon className="h-3 w-3" />
            {config.label}
        </span>
    );
};

export default function InboundDocumentsPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [orders, setOrders] = useState([]);
    
    // Filters. `status` accepts a deep-link via ?status=needs_review so
    // the "inbound documents need review" attention card on the Orders
    // dashboard can land admins directly on the filtered queue.
    const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all');
    const [filterType, setFilterType] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    
    // Link modal
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [selectedOrderId, setSelectedOrderId] = useState('');
    const [isLinking, setIsLinking] = useState(false);
    
    // View modal
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewDocument, setViewDocument] = useState(null);

    useEffect(() => {
        fetchDocuments();
        fetchOrders();
    }, [filterStatus, filterType]);

    const fetchDocuments = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);
            if (filterType && filterType !== 'all') params.append('document_type', filterType);
            
            const response = await axios.get(
                `${API_BASE}/api/inbound-documents?${params}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setDocuments(response.data || []);
        } catch (error) {
            console.error('Failed to fetch inbound documents:', error);
            toast.error('Failed to load inbound documents');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchOrders = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE}/api/orders`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setOrders(response.data || []);
        } catch (error) {
            console.error('Failed to fetch orders:', error);
        }
    };

    const handleLinkToOrder = async () => {
        if (!selectedDocument || !selectedOrderId) return;
        
        setIsLinking(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${API_BASE}/api/inbound-documents/${selectedDocument.id}/link`,
                { order_id: selectedOrderId },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Document linked to order');
            setShowLinkModal(false);
            setSelectedDocument(null);
            setSelectedOrderId('');
            fetchDocuments();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to link document');
        } finally {
            setIsLinking(false);
        }
    };

    const handleRetrySync = async (doc) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API_BASE}/api/inbound-documents/${doc.id}/retry`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (response.data.status === 'linked') {
                toast.success('Document matched and linked!');
            } else if (response.data.status === 'already_linked') {
                toast.info('Document is already linked');
            } else {
                toast.info('No matching order found');
            }
            fetchDocuments();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Retry failed');
        }
    };

    const [isReapplying, setIsReapplying] = useState(false);
    const handleReapply = async (doc) => {
        // Re-invokes the event-type handler on an already-linked doc.
        // Fixes stale processing_log entries where the field-mapping
        // ran BEFORE the workflow_variant plumbing landed and wrote to
        // the wrong supplier_confirmations row (typically production
        // instead of test_press).
        if (!doc?.matched_order_id) return;
        if (!window.confirm(
            `Re-apply parsed fields to the ${doc.workflow_variant || 'production'} confirmation for this order?\n\nThis rewrites the target supplier_confirmation and refreshes the Processing / Apply Log.`
        )) return;
        setIsReapplying(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API_BASE}/api/inbound-documents/${doc.id}/reapply`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const target = response.data?.processing_log?.target;
            toast.success(`Re-applied → ${target || 'success'}`);
            // Reload the doc in-place so the modal shows the fresh log
            const refreshed = await axios.get(
                `${API_BASE}/api/inbound-documents/${doc.id}`,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setViewDocument(refreshed.data);
            fetchDocuments();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Re-apply failed');
        } finally {
            setIsReapplying(false);
        }
    };

    const handleDelete = async (doc) => {
        if (!window.confirm(`Delete document "${doc.subject || doc.external_id}"? This cannot be undone.`)) {
            return;
        }
        
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${API_BASE}/api/inbound-documents/${doc.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Document deleted');
            fetchDocuments();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const filteredDocuments = documents.filter(doc => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            doc.subject?.toLowerCase().includes(query) ||
            doc.from_email?.toLowerCase().includes(query) ||
            doc.external_id?.toLowerCase().includes(query) ||
            doc.match_catalogue_number?.toLowerCase().includes(query) ||
            doc.match_po_number?.toLowerCase().includes(query) ||
            doc.matched_order_title?.toLowerCase().includes(query) ||
            doc.matched_order_number?.toString().includes(query) ||
            doc.event_type?.toLowerCase().includes(query)
        );
    });

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Back Button */}
            <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
            >
                <ArrowLeft className="h-5 w-5" />
                <span className="text-sm font-medium">Back</span>
            </button>

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <Inbox className="h-6 w-6 text-slate-600" />
                    <div>
                        <h1 className="text-xl font-semibold text-slate-900">Inbound Documents</h1>
                        <p className="text-sm text-slate-500">Documents received from Inbound Hub</p>
                    </div>
                </div>
                <Button variant="outline" size="sm" onClick={fetchDocuments}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                </Button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-background rounded-lg">
                <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-600">Filters:</span>
                </div>
                
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-[150px] h-9">
                        <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="linked">Linked</SelectItem>
                        <SelectItem value="needs_review">Needs Review</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="failed">Failed</SelectItem>
                    </SelectContent>
                </Select>
                
                <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-[180px] h-9">
                        <SelectValue placeholder="Document Type" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        {Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => (
                            <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                
                <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                            placeholder="Search by subject, email, catalogue #..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9 h-9"
                        />
                    </div>
                </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4 mb-4">
                {Object.entries(STATUS_CONFIG).map(([status, config]) => {
                    const count = documents.filter(d => d.status === status).length;
                    const Icon = config.icon;
                    return (
                        <div
                            key={status}
                            className={`p-3 rounded-lg border cursor-pointer hover:shadow-premium-sm transition-shadow ${
                                filterStatus === status ? 'border-slate-400 shadow-premium-sm' : 'border-border'
                            }`}
                            onClick={() => setFilterStatus(filterStatus === status ? 'all' : status)}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Icon className={`h-4 w-4 ${config.color}`} />
                                    <span className="text-sm text-slate-600">{config.label}</span>
                                </div>
                                <span className="text-lg font-semibold text-slate-900">{count}</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Documents Table */}
            <div className="border rounded-lg overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-background">
                            <TableHead className="w-[160px]">Received</TableHead>
                            <TableHead className="w-[120px]">Type</TableHead>
                            <TableHead>Subject / From</TableHead>
                            <TableHead className="w-[200px]">Order / Customer</TableHead>
                            <TableHead className="w-[120px]">Status</TableHead>
                            <TableHead className="w-[150px] text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                                    Loading documents...
                                </TableCell>
                            </TableRow>
                        ) : filteredDocuments.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                                    No documents found
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredDocuments.map((doc) => (
                                <TableRow key={doc.id} className="hover:bg-background">
                                    <TableCell className="text-sm text-slate-600">
                                        {formatDate(doc.received_at)}
                                    </TableCell>
                                    <TableCell>
                                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                                            {DOCUMENT_TYPE_LABELS[doc.document_type] || doc.document_type}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <div>
                                            <p className="text-sm font-medium text-slate-700 truncate max-w-[300px]">
                                                {doc.subject || '(No subject)'}
                                            </p>
                                            <p className="text-xs text-slate-400">{doc.from_email}</p>
                                            <div className="flex flex-wrap gap-1.5 mt-1">
                                                {doc.match_po_number && (
                                                    <span className="text-xs bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded font-medium">
                                                        PO: {doc.match_po_number}
                                                    </span>
                                                )}
                                                {doc.match_catalogue_number && (
                                                    <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">
                                                        Cat: {doc.match_catalogue_number}
                                                    </span>
                                                )}
                                                {doc.event_type && (
                                                    <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                                                        {doc.event_type.replace(/_/g, ' ')}
                                                    </span>
                                                )}
                                                {doc.parsed_quantity != null && (
                                                    <span className="text-xs bg-slate-50 text-slate-500 px-1.5 py-0.5 rounded">
                                                        Qty: {doc.parsed_quantity?.toLocaleString()}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {doc.matched_order_id ? (
                                            <button
                                                onClick={() => navigate(`/orders/${doc.matched_order_id}`)}
                                                className="text-left hover:bg-slate-100 p-1 -m-1 rounded"
                                            >
                                                <p className="text-sm font-medium text-blue-600 hover:underline">
                                                    #{doc.matched_order_number?.toString().padStart(6, '0') || '—'}
                                                </p>
                                                <p className="text-xs text-slate-500 truncate max-w-[180px]">
                                                    {doc.matched_order_title}
                                                </p>
                                            </button>
                                        ) : (
                                            <span className="text-sm text-slate-400 italic">Unmatched</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-col gap-1">
                                            <StatusBadge status={doc.status} />
                                            {doc.is_test_pressing && (
                                                <span className="text-[10px] bg-orange-50 text-orange-600 px-1.5 py-0.5 rounded font-medium">
                                                    Test pressing (qty: {doc.parsed_quantity})
                                                </span>
                                            )}
                                            {doc.needs_review_reason && (
                                                <span className="text-[10px] text-slate-400 italic">{doc.needs_review_reason}</span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center justify-end gap-1">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 w-7 p-0"
                                                title="View details"
                                                onClick={() => {
                                                    setViewDocument(doc);
                                                    setShowViewModal(true);
                                                }}
                                            >
                                                <Eye className="h-3.5 w-3.5" />
                                            </Button>
                                            
                                            {doc.status !== 'linked' && (
                                                <>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 w-7 p-0"
                                                        title="Link to order"
                                                        onClick={() => {
                                                            setSelectedDocument(doc);
                                                            setShowLinkModal(true);
                                                        }}
                                                    >
                                                        <Link2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 w-7 p-0"
                                                        title="Retry sync"
                                                        onClick={() => handleRetrySync(doc)}
                                                    >
                                                        <RefreshCw className="h-3.5 w-3.5" />
                                                    </Button>
                                                </>
                                            )}
                                            
                                            {doc.inbound_hub_item_url && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 w-7 p-0"
                                                    title="View in Inbound Hub"
                                                    onClick={() => window.open(doc.inbound_hub_item_url, '_blank')}
                                                >
                                                    <ExternalLink className="h-3.5 w-3.5" />
                                                </Button>
                                            )}
                                            
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                title="Delete"
                                                onClick={() => handleDelete(doc)}
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Link to Order Modal */}
            <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Link2 className="h-5 w-5" />
                            Link Document to Order
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedDocument && (
                        <div className="space-y-4 py-4">
                            <div className="p-3 bg-background rounded-lg">
                                <p className="text-sm font-medium text-slate-700">{selectedDocument.subject || '(No subject)'}</p>
                                <p className="text-xs text-slate-500">{selectedDocument.from_email}</p>
                                <div className="flex gap-2 mt-2">
                                    {selectedDocument.match_catalogue_number && (
                                        <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">
                                            Catalogue: {selectedDocument.match_catalogue_number}
                                        </span>
                                    )}
                                    {selectedDocument.match_po_number && (
                                        <span className="text-xs bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded">
                                            PO: {selectedDocument.match_po_number}
                                        </span>
                                    )}
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-700">Select Order</label>
                                <Select value={selectedOrderId} onValueChange={setSelectedOrderId}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Choose an order..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {orders.map(order => (
                                            <SelectItem key={order.id} value={order.id}>
                                                #{order.order_number?.toString().padStart(6, '0')} - {order.title}
                                                {order.catalogue_number && ` (${order.catalogue_number})`}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowLinkModal(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleLinkToOrder} disabled={!selectedOrderId || isLinking}>
                            {isLinking ? 'Linking...' : 'Link Document'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* View Document Modal */}
            <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="h-5 w-5" />
                            Document Details
                        </DialogTitle>
                    </DialogHeader>
                    
                    {viewDocument && (
                        <div className="space-y-4 py-4">
                            {/* Status & Type */}
                            <div className="flex items-center gap-3">
                                <StatusBadge status={viewDocument.status} />
                                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                                    {DOCUMENT_TYPE_LABELS[viewDocument.document_type] || viewDocument.document_type}
                                </span>
                            </div>
                            
                            {/* Message Info */}
                            <div className="border rounded-lg p-3 space-y-2">
                                <h4 className="text-sm font-medium text-slate-700">Email Info</h4>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                        <span className="text-slate-400">Subject:</span>
                                        <p className="font-medium">{viewDocument.subject || '—'}</p>
                                    </div>
                                    <div>
                                        <span className="text-slate-400">From:</span>
                                        <p className="font-medium">{viewDocument.from_email || '—'}</p>
                                    </div>
                                    <div>
                                        <span className="text-slate-400">Received:</span>
                                        <p className="font-medium">{formatDate(viewDocument.received_at)}</p>
                                    </div>
                                    <div>
                                        <span className="text-slate-400">External ID:</span>
                                        <p className="font-mono text-xs">{viewDocument.external_id}</p>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Match Method debug card — surfaces WHY a
                                document linked to a specific order, plus
                                partial-match warnings for multi-disc NCB
                                clearance events. Only rendered when the
                                backend supplied match_method. */}
                            {viewDocument.match_method && (
                                <div
                                    className="border rounded-lg p-3 space-y-2 bg-sky-50 border-sky-200"
                                    data-testid="match-method-card"
                                >
                                    <h4 className="text-sm font-medium text-sky-800">Match Method</h4>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div>
                                            <span className="text-slate-500">Method:</span>
                                            <p className="font-medium" data-testid="match-method-label">
                                                {{
                                                    'order_number': 'Order # (Sony Graphic Upload)',
                                                    'disc_sub_catalogue_set': 'Disc sub-catalogue set',
                                                    'catalogue_number': 'Catalogue #',
                                                    'disc_sub_catalogue_number': 'Disc sub-catalogue #',
                                                    'po_number': 'PO # (Sony order)',
                                                }[viewDocument.match_method] || viewDocument.match_method}
                                            </p>
                                        </div>
                                        {viewDocument.match_expected_count != null && (
                                            <div>
                                                <span className="text-slate-500">Match count:</span>
                                                <p className="font-medium" data-testid="match-count">
                                                    {viewDocument.match_count ?? 0}/{viewDocument.match_expected_count}
                                                    {viewDocument.match_count === viewDocument.match_expected_count
                                                        ? <span className="ml-1 text-emerald-600">✓</span>
                                                        : <span className="ml-1 text-amber-600">⚠ partial</span>}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    {viewDocument.matched_catalogue_numbers?.length > 0 && (
                                        <div className="text-sm">
                                            <span className="text-slate-500">Matched catalogue numbers:</span>
                                            <div className="flex flex-wrap gap-1 mt-1" data-testid="matched-cat-list">
                                                {viewDocument.matched_catalogue_numbers.map((c) => (
                                                    <span
                                                        key={c}
                                                        className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono bg-white border border-sky-200 text-slate-700"
                                                    >
                                                        {c}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Match Info */}
                            <div className="border rounded-lg p-3 space-y-2">
                                <h4 className="text-sm font-medium text-slate-700">Match Info</h4>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                        <span className="text-slate-400">Catalogue #:</span>
                                        <p className="font-medium">{viewDocument.match_catalogue_number || '—'}</p>
                                    </div>
                                    <div>
                                        <span className="text-slate-400">PO #:</span>
                                        <p className="font-medium">{viewDocument.match_po_number || '—'}</p>
                                    </div>
                                    <div>
                                        <span className="text-slate-400">Order Reference:</span>
                                        <p className="font-medium">{viewDocument.match_order_reference || '—'}</p>
                                    </div>
                                    <div>
                                        <span className="text-slate-400">Confidence:</span>
                                        <p className="font-medium">{viewDocument.match_confidence ? `${(viewDocument.match_confidence * 100).toFixed(0)}%` : '—'}</p>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Linked Order */}
                            {viewDocument.matched_order_id && (
                                <div className="border rounded-lg p-3 space-y-2 bg-emerald-50 border-emerald-200">
                                    <h4 className="text-sm font-medium text-emerald-700">Linked Order</h4>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-medium text-slate-700">
                                                #{viewDocument.matched_order_number?.toString().padStart(6, '0')}
                                            </p>
                                            <p className="text-sm text-slate-500">{viewDocument.matched_order_title}</p>
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                                setShowViewModal(false);
                                                navigate(`/orders/${viewDocument.matched_order_id}`);
                                            }}
                                        >
                                            View Order
                                        </Button>
                                    </div>
                                    {viewDocument.linked_by_name && (
                                        <p className="text-xs text-slate-500">
                                            Linked by {viewDocument.linked_by_name} on {formatDate(viewDocument.linked_at)}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Processing / Apply Log — surfaces what
                                the event handler actually did with the
                                parsed data. "Linked" only tells us the
                                doc matched an order; this card tells
                                admins whether the fields were persisted
                                to the target confirmation. */}
                            {viewDocument.processing_log && (
                                <div
                                    className={`border rounded-lg p-3 space-y-2 ${
                                        viewDocument.processing_log.result === 'success'
                                            ? 'bg-emerald-50 border-emerald-200'
                                            : 'bg-red-50 border-red-200'
                                    }`}
                                    data-testid="processing-log-card"
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <h4
                                            className={`text-sm font-medium ${
                                                viewDocument.processing_log.result === 'success'
                                                    ? 'text-emerald-700'
                                                    : 'text-red-700'
                                            }`}
                                        >
                                            Processing / Apply Log
                                        </h4>
                                        <div className="flex items-center gap-2">
                                            {viewDocument.matched_order_id && viewDocument.event_type && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="h-6 text-[11px] px-2"
                                                    onClick={() => handleReapply(viewDocument)}
                                                    disabled={isReapplying}
                                                    data-testid="reapply-fields-btn"
                                                    title="Re-run the field-mapping handler against the current workflow_variant. Fixes docs that were applied before the TP variant plumbing landed."
                                                >
                                                    {isReapplying ? 'Re-applying…' : 'Re-apply'}
                                                </Button>
                                            )}
                                            <span
                                                className={`text-xs font-semibold uppercase px-2 py-0.5 rounded ${
                                                    viewDocument.processing_log.result === 'success'
                                                        ? 'bg-emerald-100 text-emerald-800'
                                                        : 'bg-red-100 text-red-800'
                                                }`}
                                                data-testid="processing-log-result"
                                            >
                                                {viewDocument.processing_log.result}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="text-xs text-slate-700 space-y-1">
                                        {viewDocument.matched_order_number != null && (
                                            <p>
                                                <span className="text-slate-500">Matched order:</span>{' '}
                                                <span className="font-medium">
                                                    #{viewDocument.matched_order_number.toString().padStart(6, '0')}
                                                </span>
                                            </p>
                                        )}
                                        {viewDocument.processing_log.variant && (
                                            <p>
                                                <span className="text-slate-500">Variant:</span>{' '}
                                                <span className="font-medium">{viewDocument.processing_log.variant}</span>
                                            </p>
                                        )}
                                        {viewDocument.processing_log.target && (
                                            <p>
                                                <span className="text-slate-500">Target:</span>{' '}
                                                <span className="font-medium">{viewDocument.processing_log.target}</span>
                                                {viewDocument.processing_log.target_action && (
                                                    <span className="text-slate-500 ml-1">
                                                        ({viewDocument.processing_log.target_action})
                                                    </span>
                                                )}
                                            </p>
                                        )}
                                    </div>
                                    {viewDocument.processing_log.steps?.length > 0 && (
                                        <div className="mt-2 space-y-1" data-testid="processing-log-steps">
                                            {viewDocument.processing_log.steps.map((step, idx) => (
                                                <div
                                                    key={idx}
                                                    className="flex items-center justify-between text-xs bg-white/70 rounded px-2 py-1 border border-emerald-100"
                                                >
                                                    <span className="text-slate-700">
                                                        <span className="font-medium">{step.label || step.field}:</span>{' '}
                                                        <span className="font-mono">
                                                            {step.value === null || step.value === undefined
                                                                ? '—'
                                                                : String(step.value)}
                                                        </span>
                                                    </span>
                                                    <span
                                                        className={`ml-2 text-[11px] font-semibold uppercase ${
                                                            step.outcome === 'updated'
                                                                ? 'text-emerald-600'
                                                                : step.outcome === 'skipped'
                                                                ? 'text-slate-400'
                                                                : 'text-red-600'
                                                        }`}
                                                    >
                                                        → {step.outcome || 'updated'}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {viewDocument.processing_log.reason && (
                                        <p className="text-xs text-red-700 mt-1">
                                            <span className="font-medium">Reason:</span> {viewDocument.processing_log.reason}
                                        </p>
                                    )}
                                    {viewDocument.processing_log.message && viewDocument.processing_log.result === 'success' && (
                                        <p className="text-xs text-emerald-700 mt-1 italic">
                                            {viewDocument.processing_log.message}
                                        </p>
                                    )}
                                </div>
                            )}
                            
                            {/* Attachments */}
                            {viewDocument.attachments && viewDocument.attachments.length > 0 && (
                                <div className="border rounded-lg p-3 space-y-2">
                                    <h4 className="text-sm font-medium text-slate-700">Attachments ({viewDocument.attachments.length})</h4>
                                    <div className="space-y-2">
                                        {viewDocument.attachments.map((att, idx) => (
                                            <div key={idx} className="flex items-center justify-between p-2 bg-background rounded">
                                                <div className="flex items-center gap-2">
                                                    <FileText className="h-4 w-4 text-slate-400" />
                                                    <div>
                                                        <p className="text-sm font-medium">{att.filename}</p>
                                                        <p className="text-xs text-slate-400">
                                                            {att.content_type} • {(att.size_bytes / 1024).toFixed(1)} KB
                                                        </p>
                                                    </div>
                                                </div>
                                                {att.download_url && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => window.open(att.download_url, '_blank')}
                                                    >
                                                        <Download className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Sync Info */}
                            <div className="text-xs text-slate-400 space-y-1">
                                <p>Sync attempts: {viewDocument.sync_attempts}</p>
                                {viewDocument.last_sync_error && (
                                    <p className="text-red-500">Last error: {viewDocument.last_sync_error}</p>
                                )}
                                {viewDocument.inbound_hub_item_url && (
                                    <a 
                                        href={viewDocument.inbound_hub_item_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-500 hover:underline flex items-center gap-1"
                                    >
                                        View in Inbound Hub <ExternalLink className="h-3 w-3" />
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowViewModal(false)}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
